import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { getProductDetailsEmptyAvailabilitiesKey } from '../../../../services/modules/productDetails/productDetails.keys';
import useProductActionsStore from '../../../../stores/ProductActionsStore/productActions.store';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import useOrderBook from '../../../order-book/hooks/useOrderBook';
import { ordersRepository } from '../../../orders/repositories/ordersRepository/orders.repository';
import { AvailabilityEnum } from '../../ProductTypes';
import { useProductDetailsOrderData } from '../contexts/ProductDetailsOrderData.context';

interface UseProductDetailsEmptyAvailabilityParams {
  productId: string;
  orderId?: string;
  productColorId: string;
}

const useProductDetailsEmptyAvailability = ({
  orderId,
  productId,
  productColorId,
}: UseProductDetailsEmptyAvailabilityParams) => {
  const { getEmptyAvailabilities } = useProductDetailsOrderData();
  const emptyAvailabilities = getEmptyAvailabilities(orderId);

  const hasEmptyAvailability = !!emptyAvailabilities?.find(
    (emptyProductColor) => emptyProductColor.productColorId === productColorId,
  );

  const setDeletedProductColors = useProductActionsStore((store) => store.setDeletedProductColors);

  const { team } = useApplicationContext();
  const queryClient = useQueryClient();

  const { allOrders } = useOrderBook();
  const hasSuggestedOrderInOrderBook = allOrders.find((order) => order.status === 'SUGGESTED');

  const handleRemoveOrderProductWithEmptyAvailability = useCallback(async () => {
    const response = await ordersRepository(orderId).deleteOrderProduct({
      productId,
      availabilities: [AvailabilityEnum.EMPTY],
      productColorId,
    });

    if (!response.ok) {
      return;
    }

    const emptyAvailabilitiesQueryKey = [
      team?.id,
      ...getProductDetailsEmptyAvailabilitiesKey({
        orderId,
        productId,
      }),
    ];
    await queryClient.refetchQueries({
      queryKey: emptyAvailabilitiesQueryKey,
    });

    if (hasSuggestedOrderInOrderBook) {
      setDeletedProductColors([productColorId]);
    }
  }, [
    orderId,
    productId,
    productColorId,
    team?.id,
    queryClient,
    hasSuggestedOrderInOrderBook,
    setDeletedProductColors,
  ]);

  return {
    handleRemoveOrderProductWithEmptyAvailability,
    hasEmptyAvailability,
  };
};

export default useProductDetailsEmptyAvailability;
